import { useState, useCallback, useRef } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

interface ConfirmState {
  isOpen: boolean
  message: string
  title?: string
  confirmLabel?: string
  variant?: 'danger' | 'primary'
}

export function useConfirmDialog() {
  const [state, setState] = useState<ConfirmState>({ isOpen: false, message: '' })
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback(
    (message: string, options?: { title?: string; confirmLabel?: string; variant?: 'danger' | 'primary' }) => {
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve
        setState({
          isOpen: true,
          message,
          title: options?.title,
          confirmLabel: options?.confirmLabel,
          variant: options?.variant,
        })
      })
    },
    [],
  )

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true)
    resolveRef.current = null
    setState((s) => ({ ...s, isOpen: false }))
  }, [])

  const handleClose = useCallback(() => {
    resolveRef.current?.(false)
    resolveRef.current = null
    setState((s) => ({ ...s, isOpen: false }))
  }, [])

  const dialog = (
    <ConfirmDialog
      isOpen={state.isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      message={state.message}
      title={state.title}
      confirmLabel={state.confirmLabel}
      variant={state.variant}
    />
  )

  return { dialog, confirm }
}
